/*  ModuleListPanel — left panel of the Course Builder.
    ────────────────────────────────────────────────────────────
    Renders modules in order with:
      • + Add module
      • Click module title to select (loads lessons on right)
      • Rename inline (double-click)
      • Lock/unlock toggle
      • Up/Down reorder arrows
      • Delete
    Uses button-based reorder for MVP (accessible + robust). True DnD
    can be added later without changing the API contract.
*/
import React, { useState } from "react";
import { toast } from "sonner";
import {
    Plus, Trash, Lock, LockOpen, CaretUp, CaretDown, PencilSimple, Books, Check,
    GraduationCap,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { confirmDialog } from "@/lib/confirm";


export const FINAL_EXAM_ID = "__final_exam__";


export default function ModuleListPanel({
    course, modules, activeModuleId, onSelectModule, onModulesChange, readOnly,
}) {
    const [busy, setBusy] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editValue, setEditValue] = useState("");

    const addModule = async () => {
        try {
            setBusy(true);
            const { data } = await api.post(`/course-builder/courses/${course.id}/modules`, {
                title: `Module ${(modules?.length || 0) + 1}`,
                is_locked: false,
            });
            const next = [...(modules || []), { ...data, lessons: [] }];
            onModulesChange(next);
            onSelectModule(data.id);
            toast.success("Module added");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't add module.");
        } finally {
            setBusy(false);
        }
    };

    const persistModule = async (m, patch) => {
        try {
            const { data } = await api.put(`/course-builder/modules/${m.id}`, patch);
            const next = modules.map((row) => (row.id === m.id ? { ...row, ...data } : row));
            onModulesChange(next);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't update module.");
        }
    };

    const toggleLock = (m) => persistModule(m, { is_locked: !m.is_locked });

    const startRename = (m) => {
        setEditingId(m.id);
        setEditValue(m.title);
    };

    const commitRename = async () => {
        if (!editingId) return;
        const m = modules.find((row) => row.id === editingId);
        const next = (editValue || "").trim();
        if (m && next && next !== m.title && next.length >= 2) {
            await persistModule(m, { title: next });
        }
        setEditingId(null);
        setEditValue("");
    };

    const moveModule = async (idx, dir) => {
        const to = idx + dir;
        if (to < 0 || to >= modules.length) return;
        const reordered = [...modules];
        [reordered[idx], reordered[to]] = [reordered[to], reordered[idx]];
        // Optimistic
        onModulesChange(reordered.map((m, i) => ({ ...m, order: i })));
        try {
            await api.post(`/course-builder/courses/${course.id}/modules/reorder`, {
                items: reordered.map((m, i) => ({ id: m.id, order: i })),
            });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't reorder.");
        }
    };

    const deleteModule = async (m) => {
        const ok = await confirmDialog({
            title: "Delete module?",
            description: `"${m.title}" and all its lessons will be removed.`,
            confirmLabel: "Delete",
            variant: "destructive",
        });
        if (!ok) return;
        try {
            await api.delete(`/course-builder/modules/${m.id}`);
            const next = modules.filter((row) => row.id !== m.id);
            onModulesChange(next);
            if (activeModuleId === m.id) onSelectModule(next[0]?.id || null);
            toast.success("Module deleted");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't delete module.");
        }
    };

    return (
        <aside className="w-full lg:w-64 xl:w-72 shrink-0 border-r border-border bg-card flex flex-col" data-testid="module-list-panel">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Modules</div>
                {!readOnly && (
                    <button
                        type="button"
                        onClick={addModule}
                        disabled={busy}
                        className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md bg-primary text-primary-foreground text-[11px] font-bold hover:bg-primary/90 transition-colors disabled:opacity-60"
                        data-testid="module-add-btn"
                    >
                        <Plus size={11} weight="bold" /> Add
                    </button>
                )}
            </div>

            <div className="flex-1 overflow-y-auto p-2">
                {(!modules || modules.length === 0) ? (
                    <div className="text-center py-8 text-muted-foreground">
                        <Books size={24} weight="duotone" className="mx-auto mb-2" />
                        <div className="text-xs">{readOnly ? "No modules yet." : "Add your first module to get started."}</div>
                    </div>
                ) : modules.map((m, idx) => {
                    const isActive = m.id === activeModuleId;
                    const isEditing = editingId === m.id;
                    return (
                        <div
                            key={m.id}
                            className={`group rounded-lg mb-1 border transition-all ${
                                isActive
                                    ? "bg-primary/5 border-primary/30 shadow-[0_0_0_2px_hsl(var(--primary)/0.10)]"
                                    : "border-transparent hover:bg-secondary/60"
                            }`}
                            data-testid={`module-row-${m.id}`}
                        >
                            <div className="flex items-center gap-1.5 p-2">
                                <div
                                    className={`w-6 h-6 shrink-0 rounded-md text-[10px] font-bold tabular-nums grid place-items-center ${
                                        isActive ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"
                                    }`}
                                >
                                    {idx + 1}
                                </div>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        autoFocus
                                        value={editValue}
                                        onChange={(e) => setEditValue(e.target.value)}
                                        onBlur={commitRename}
                                        onKeyDown={(e) => { if (e.key === "Enter") commitRename(); if (e.key === "Escape") { setEditingId(null); setEditValue(""); } }}
                                        className="flex-1 min-w-0 bg-background border border-primary rounded px-1.5 py-0.5 text-xs font-semibold"
                                        maxLength={140}
                                        data-testid={`module-rename-input-${m.id}`}
                                    />
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => onSelectModule(m.id)}
                                        onDoubleClick={() => !readOnly && startRename(m)}
                                        className="flex-1 min-w-0 text-left text-xs font-semibold truncate hover:text-primary transition-colors"
                                        data-testid={`module-select-${m.id}`}
                                    >
                                        {m.title}
                                    </button>
                                )}
                                {m.is_locked && (
                                    <Lock size={11} weight="fill" className="text-amber-600 shrink-0" />
                                )}
                            </div>

                            {isActive && !readOnly && !isEditing && (
                                <div className="flex items-center gap-1 px-2 pb-2 pt-0">
                                    <IconBtn onClick={() => startRename(m)} title="Rename" testid={`module-rename-${m.id}`}>
                                        <PencilSimple size={10} weight="bold" />
                                    </IconBtn>
                                    <IconBtn onClick={() => toggleLock(m)} title={m.is_locked ? "Unlock module" : "Lock module"} testid={`module-lock-${m.id}`}>
                                        {m.is_locked ? <LockOpen size={10} weight="bold" /> : <Lock size={10} weight="bold" />}
                                    </IconBtn>
                                    <IconBtn onClick={() => moveModule(idx, -1)} disabled={idx === 0} title="Move up" testid={`module-up-${m.id}`}>
                                        <CaretUp size={10} weight="bold" />
                                    </IconBtn>
                                    <IconBtn onClick={() => moveModule(idx, 1)} disabled={idx === modules.length - 1} title="Move down" testid={`module-down-${m.id}`}>
                                        <CaretDown size={10} weight="bold" />
                                    </IconBtn>
                                    <IconBtn onClick={() => deleteModule(m)} tone="danger" title="Delete module" testid={`module-delete-${m.id}`}>
                                        <Trash size={10} weight="bold" />
                                    </IconBtn>
                                    <span className="ml-auto text-[9px] text-muted-foreground font-semibold">
                                        {(m.lessons || []).length} lesson{(m.lessons || []).length === 1 ? "" : "s"}
                                    </span>
                                </div>
                            )}
                        </div>
                    );
                })}

                {/* Final Exam — sits at the bottom of the module list.
                    Rendered only when the course has has_final_exam enabled.
                    Clicking swaps the right-hand panel into FinalExamBuilder. */}
                {course?.has_final_exam ? (
                    <div className="mt-3 pt-3 border-t border-dashed border-border">
                        <div className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground px-2 mb-1">
                            Course Capstone
                        </div>
                        <button
                            type="button"
                            onClick={() => onSelectModule(FINAL_EXAM_ID)}
                            className={`w-full group rounded-lg mb-1 border transition-all text-left ${
                                activeModuleId === FINAL_EXAM_ID
                                    ? "bg-rose-50 border-rose-300 shadow-[0_0_0_2px_hsl(354_63%_63%/0.15)]"
                                    : "border-transparent hover:bg-rose-50/50"
                            }`}
                            data-testid="module-select-final-exam"
                        >
                            <div className="flex items-center gap-1.5 p-2">
                                <div className={`w-6 h-6 shrink-0 rounded-md grid place-items-center ${
                                    activeModuleId === FINAL_EXAM_ID
                                        ? "bg-rose-600 text-white"
                                        : "bg-rose-100 text-rose-700"
                                }`}>
                                    <GraduationCap size={12} weight="fill" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-xs font-bold truncate">Final Exam</div>
                                    <div className="text-[10px] text-muted-foreground">
                                        {course.final_exam_question_count ? `${course.final_exam_question_count} questions` : "Questions unset"}
                                        {course.final_exam_pass_score ? ` · ${Math.round(course.final_exam_pass_score * 100)}% pass` : ""}
                                    </div>
                                </div>
                            </div>
                        </button>
                    </div>
                ) : null}
            </div>
        </aside>
    );
}


function IconBtn({ children, onClick, title, disabled, tone, testid }) {
    const toneCls = tone === "danger"
        ? "text-rose-600 hover:bg-rose-50 border-rose-200"
        : "text-muted-foreground hover:text-foreground hover:bg-secondary border-border";
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            title={title}
            data-testid={testid}
            className={`w-6 h-6 grid place-items-center rounded border ${toneCls} transition-colors disabled:opacity-40 disabled:cursor-not-allowed`}
        >
            {children}
        </button>
    );
}
